import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Stack from '@mui/material/Stack'

import { Text } from '@mxenabled/mxui'

import { useGlobalCopyStore } from '../../common'
import { SpendingData } from '../utils/SpendingData'
import ListItemButton from '@mui/material/ListItemButton'
import { formatCurrency } from '../../core/utils/NumberFormatting'

interface SpendingListProps {
  data: SpendingData
  onSelected: (id: string, isSubcategory?: boolean) => void
  selectedId?: string
  title: string
}

const SpendingList: React.FC<SpendingListProps> = ({
  data,
  onSelected,
  selectedId = '',
  title,
}) => {
  const { spending: spendingCopy } = useGlobalCopyStore()
  const selectedCategory = data.categoryData.find((parent) => parent.guid === selectedId)

  return (
    <Stack direction="column" justifyContent="center" maxWidth={400} minWidth={275} width="100%">
      <Text bold={true} id="spending-list-title" sx={{ py: 12 }} variant="h3">
        {`${title} ${spendingCopy.by_category}`}
      </Text>

      <Divider aria-hidden="true" />

      <List aria-labelledby="spending-list-title">
        {data.categoryData.map((item) => (
          <React.Fragment key={item.guid}>
            <ListItem
              disableGutters={true}
              disablePadding={true}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                minHeight: 44,
              }}
            >
              <ListItemButton
                aria-expanded={Boolean(selectedCategory?.guid === item.guid)}
                id={`spending-list-category-${item.guid}`}
                onClick={() => onSelected(item.guid)}
                sx={{ minHeight: 44, px: 8, py: 0 }}
              >
                <Box
                  aria-hidden="true"
                  borderRadius={4}
                  flexShrink={0}
                  height={8}
                  role="presentation"
                  sx={{ backgroundColor: item.color, mr: 8 }}
                  width={8}
                />
                <Stack
                  alignItems="center"
                  direction="row"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Text bold={selectedId === item.guid}>{item.name}</Text>
                  <Text bold={selectedId === item.guid} variant="body1">
                    {formatCurrency(item.amount, '0,0.00')}
                  </Text>
                </Stack>
              </ListItemButton>
            </ListItem>

            {item.categoryTotals && item.categoryTotals.length > 0 && (
              <Collapse in={Boolean(selectedCategory?.guid === item.guid)} unmountOnExit={true}>
                <List sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  {selectedCategory?.categoryTotals?.map((subItem) => (
                    <ListItem
                      key={subItem.guid}
                      sx={{
                        pl: 12,
                        py: 0,
                      }}
                    >
                      <ListItemButton
                        id={`spending-list-subcategory-${subItem.guid}`}
                        onClick={() => onSelected(subItem.guid, true)}
                        sx={{ minHeight: 44, px: 8, py: 0 }}
                      >
                        <Stack direction="row" justifyContent="space-between" width="100%">
                          <Text>{subItem.name} </Text>
                          <Text>{formatCurrency(subItem.amount, '0,0.00')}</Text>
                        </Stack>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>

      <Text bold={true} sx={{ mt: 12, pr: 8, textAlign: 'right' }}>
        {`${spendingCopy.total}: ${formatCurrency(data.totalAmount, '0,0.00')}`}
      </Text>
    </Stack>
  )
}

export default observer(SpendingList)
